import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import PriceChart from './components/PriceChart';
import PTSWizardPanel from './components/PTSWizardPanel';
import TradingPanel from './components/TradingPanel';
import MarketDataTable from './components/MarketDataTable';
import WatchlistWidget from './components/WatchlistWidget';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const TradingDashboard = () => {
  const [selectedSymbol, setSelectedSymbol] = useState('BTC/USD');
  const [timeframe, setTimeframe] = useState('1h');
  const [currentPrice, setCurrentPrice] = useState(43275.50);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activePanel, setActivePanel] = useState('chart');

  // Mock price updates
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPrice(prev => {
        const change = (Math.random() - 0.5) * 100;
        return Math.max(prev + change, 1000);
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleSymbolSelect = (symbol) => {
    setSelectedSymbol(symbol);
    // Update current price based on symbol
    const prices = {
      'BTC/USD': 43275.50,
      'ETH/USD': 2650.75,
      'ADA/USD': 0.52,
      'SOL/USD': 98.45,
      'DOT/USD': 7.85,
      'MATIC/USD': 0.89
    };
    setCurrentPrice(prices?.[symbol] || 1000);
  };

  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Main Content */}
      <main className="pt-[60px] pb-[80px] md:pb-4">
        <div className="container mx-auto px-4 py-6">
          {/* Dashboard Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">Trading Dashboard</h1>
              <p className="text-muted-foreground">
                Advanced cryptocurrency trading with PTS-WIZARD-V3 algorithm
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleFullscreen}
              >
                <Icon name={isFullscreen ? "Minimize2" : "Maximize2"} size={16} />
                {isFullscreen ? 'Exit' : 'Fullscreen'}
              </Button>
              <div className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
                <span className="text-sm text-muted-foreground">Market Open</span>
              </div>
            </div>
          </div>

          {/* Mobile Panel Selector */}
          <div className="md:hidden mb-4">
            <div className="flex bg-muted rounded-lg p-1">
              <Button
                variant={activePanel === 'chart' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActivePanel('chart')}
                className="flex-1"
              >
                <Icon name="BarChart3" size={16} />
                Chart
              </Button>
              <Button
                variant={activePanel === 'wizard' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActivePanel('wizard')}
                className="flex-1"
              >
                <Icon name="Zap" size={16} />
                Wizard
              </Button>
              <Button
                variant={activePanel === 'trade' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActivePanel('trade')}
                className="flex-1"
              >
                <Icon name="TrendingUp" size={16} />
                Trade
              </Button>
              <Button
                variant={activePanel === 'market' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActivePanel('market')}
                className="flex-1"
              >
                <Icon name="List" size={16} />
                Market
              </Button>
            </div>
          </div>

          {/* Dashboard Grid */}
          <div className={`grid gap-6 ${isFullscreen ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-12'}`}>
            {/* Main Chart Area */}
            <div className={`${isFullscreen ? 'col-span-1' : 'lg:col-span-8'} ${activePanel === 'chart' || !activePanel ? 'block' : 'hidden md:block'}`}>
              <div className="grid gap-6 grid-cols-1">
                {/* Price Chart */}
                <div className="h-[500px]">
                  <PriceChart
                    selectedSymbol={selectedSymbol}
                    timeframe={timeframe}
                    onTimeframeChange={handleTimeframeChange}
                  />
                </div>

                {/* PTS-WIZARD-V3 Panel */}
                {!isFullscreen && (
                  <div className={`h-[400px] ${activePanel === 'wizard' || !activePanel ? 'block' : 'hidden md:block'}`}>
                    <PTSWizardPanel selectedSymbol={selectedSymbol} />
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            {!isFullscreen && (
              <div className="lg:col-span-4 space-y-6">
                {/* Trading Panel */}
                <div className={`h-[500px] ${activePanel === 'trade' || !activePanel ? 'block' : 'hidden md:block'}`}>
                  <TradingPanel
                    selectedSymbol={selectedSymbol}
                    currentPrice={currentPrice}
                  />
                </div>

                {/* Watchlist Widget */}
                <div className={`h-[400px] ${activePanel === 'market' || !activePanel ? 'block' : 'hidden md:block'}`}>
                  <WatchlistWidget
                    onSymbolSelect={handleSymbolSelect}
                    selectedSymbol={selectedSymbol}
                  />
                </div>
              </div>
            )}

            {/* Market Data Table - Full Width */}
            <div className={`${isFullscreen ? 'col-span-1' : 'lg:col-span-12'} ${activePanel === 'market' || !activePanel ? 'block' : 'hidden md:block'}`}>
              <div className="h-[500px]">
                <MarketDataTable
                  onSymbolSelect={handleSymbolSelect}
                  selectedSymbol={selectedSymbol}
                />
              </div>
            </div>

            {/* PTS-WIZARD-V3 Panel - Fullscreen Mode */}
            {isFullscreen && (
              <div className="col-span-1">
                <div className="h-[400px]">
                  <PTSWizardPanel selectedSymbol={selectedSymbol} />
                </div>
              </div>
            )}
          </div>

          {/* Quick Stats Bar */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="TrendingUp" size={16} className="text-success" />
                <span className="text-sm text-muted-foreground">24h Volume</span>
              </div>
              <div className="text-lg font-semibold text-foreground">$28.5B</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Activity" size={16} className="text-primary" />
                <span className="text-sm text-muted-foreground">Active Trades</span>
              </div>
              <div className="text-lg font-semibold text-foreground">12</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Zap" size={16} className="text-accent" />
                <span className="text-sm text-muted-foreground">PTS Signals</span>
              </div>
              <div className="text-lg font-semibold text-foreground">24</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Target" size={16} className="text-warning" />
                <span className="text-sm text-muted-foreground">Success Rate</span>
              </div>
              <div className="text-lg font-semibold text-success">73.5%</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TradingDashboard;